import type { ChatSummary } from '../../api/types';
import type { RootStackParamList } from '../../navigation/types';

/**
 * Параметры перехода в чат из его карточки. Жило внутри `ChatListScreen` (ARCH-1) — до тех пор,
 * пока переход к оригиналу сохранённого сообщения (стори 8.3) не понадобился и с экрана самого
 * чата: подпись «Из: …» ведёт в другой разговор, и собирать те же параметры вторым, чуть иным
 * способом значило бы завести второе представление о том, что такое «открытый чат».
 */
export function chatNavParamsFromSummary(chat: ChatSummary): RootStackParamList['Chat'] {
  const base: RootStackParamList['Chat'] = {
    chatId: chat.id,
    title: chat.title ?? '',
    isGroup: chat.type === 'GROUP',
    isChannel: chat.type === 'CHANNEL',
    // «Избранное» — единственный чат, который клиент узнаёт только по типу: заголовка у него
    // нет, собеседника нет, и без этого флага экран принял бы его за личную переписку.
    isSaved: chat.type === 'SAVED',
    canPost: chat.canPost ?? chat.type !== 'CHANNEL',
    canComment: chat.allowSubscriberComments ?? false,
    canReact: chat.allowSubscriberReactions ?? true,
    canManage: chat.canManage ?? false,
    myRole: chat.myRole ?? null,
    channelDescription: chat.description ?? null,
    memberCount: typeof chat.memberCount === 'number' ? chat.memberCount : null,
    // В base, а не в ветке DIRECT ниже: у личного чата без peerUserId та ветка не срабатывает,
    // и объяснение пустоты пропало бы именно там, где оно нужнее всего (стори 4.1).
    directMessagesClearedAt: chat.type === 'DIRECT' ? chat.directMessagesClearedAt ?? null : null,
  };
  if (chat.type === 'DIRECT' && chat.peerUserId) {
    return {
      ...base,
      peerUserId: chat.peerUserId,
      peerHasAvatar: Boolean(chat.peerHasAvatar),
      peerAvatarRevision:
        typeof chat.peerAvatarRevision === 'number' && Number.isFinite(chat.peerAvatarRevision)
          ? chat.peerAvatarRevision
          : 0,
      peerAvatarShape: chat.peerAvatarShape ?? null,
    };
  }
  if (chat.type === 'GROUP' || chat.type === 'CHANNEL') {
    return {
      ...base,
      channelSlug: chat.type === 'CHANNEL' ? chat.channelSlug ?? null : undefined,
      chatHasAvatar: Boolean(chat.chatHasAvatar),
      chatAvatarRevision:
        typeof chat.chatAvatarRevision === 'number' && Number.isFinite(chat.chatAvatarRevision)
          ? chat.chatAvatarRevision
          : 0,
      chatAvatarShape: chat.chatAvatarShape ?? null,
      ...(chat.type === 'GROUP' && chat.meetingToken ? { meetingToken: chat.meetingToken } : {}),
    };
  }
  return base;
}
